import { Container, Sprite, type Texture } from 'pixi.js';
import { DIGIT_WIDTH, type DigitSpriteKey, digitKey } from '@/sprites/digits';

/**
 * Numbers that float off something that was hit.
 *
 * The only feedback that says how much a swing was worth, which is the
 * difference between a weapon feeling like an upgrade and feeling like a
 * reskin. They rise and fade rather than sitting still, so two hits on the
 * same enemy do not stack into an unreadable smear.
 */

const LIFETIME = 0.6;
const RISE = 18;
/** Gap between digits, in pixels. */
const KERNING = 1;

interface FloatingNumber {
  sprites: Sprite[];
  x: number;
  y: number;
  life: number;
}

export class DamageNumbers {
  readonly container = new Container();
  private readonly active: FloatingNumber[] = [];
  private readonly pool: Sprite[] = [];

  constructor(private readonly textures: Readonly<Record<DigitSpriteKey, Texture>>) {}

  spawn(value: number, x: number, y: number): void {
    const digits = Math.max(0, Math.round(value)).toString().split('');
    const width = digits.length * DIGIT_WIDTH + (digits.length - 1) * KERNING;

    const sprites = digits.map((digit, index) => {
      const sprite = this.take();
      sprite.texture = this.textures[digitKey(Number(digit))]!;
      sprite.position.set(index * (DIGIT_WIDTH + KERNING) - width / 2, 0);
      sprite.visible = true;
      sprite.alpha = 1;
      return sprite;
    });

    this.active.push({ sprites, x, y, life: 0 });
  }

  update(dt: number): void {
    for (let i = this.active.length - 1; i >= 0; i--) {
      const entry = this.active[i]!;
      entry.life += dt;

      const progress = entry.life / LIFETIME;
      if (progress >= 1) {
        for (const sprite of entry.sprites) this.release(sprite);
        this.active.splice(i, 1);
        continue;
      }

      const y = entry.y - RISE * progress;
      for (const sprite of entry.sprites) {
        sprite.y = Math.round(y);
        // Held solid for the first half, so the number is readable before it
        // starts to go — a fade that begins at once is a number nobody reads.
        sprite.alpha = progress < 0.5 ? 1 : 1 - (progress - 0.5) * 2;
      }

      for (const [index, sprite] of entry.sprites.entries()) {
        sprite.x = Math.round(
          entry.x + index * (DIGIT_WIDTH + KERNING) -
            (entry.sprites.length * DIGIT_WIDTH + (entry.sprites.length - 1) * KERNING) / 2,
        );
      }
    }
  }

  private take(): Sprite {
    const pooled = this.pool.pop();
    if (pooled) return pooled;
    const sprite = new Sprite();
    this.container.addChild(sprite);
    return sprite;
  }

  private release(sprite: Sprite): void {
    sprite.visible = false;
    this.pool.push(sprite);
  }

  destroy(): void {
    this.container.destroy({ children: true });
  }
}
